// Reference normalization: many spellings, ONE canonical ref.
//
//   "Bamidbar 13:16" / "Num. 13:16" / "Numbers 13:16" -> "Numbers 13:16"
//   "Chagiga 12a" -> "Chagigah 12a"
//   "Rashi Bamidbar 13:16" -> "Rashi on Numbers 13:16"
//
// DESIGN (accuracy-lock): this is a deterministic MAPPING TABLE + parser. There
// is NO fuzzy matching - an alias either matches exactly (after lowercasing /
// punctuation-stripping / nikud-stripping) or the ref FAILS LOUD with
// { valid: false, error }. Bad refs are never silently accepted, and similar
// names (Rambam vs Ramban, Rashi vs Rashbam) can never collapse into each
// other because matching is exact, never nearest-match.
//
// Canonical spellings follow Sefaria's English index titles, since Sefaria is
// the live text provider (the canonical ref doubles as the fetchable ref).

import type {
  NormalizedRef,
  NormalizedRefResult,
  PassageAddress,
  Passage,
  SourceCategory,
} from "./sourceGraph.ts";
import { stripHebrew, gematria } from "./citations.ts";

// ---------------------------------------------------------------------------
// The works table (extensible). `addressing` drives how the numeric tail is
// interpreted; `aliases` hold transliterations, abbreviations and Hebrew-script
// names. Keep entries alphabetical-ish by corpus for maintainability.
// ---------------------------------------------------------------------------

export type Addressing = "chapter_verse" | "daf_amud" | "siman_seif" | "chapter_mishnah";

export interface KnownWork {
  id: string; // stable work id, e.g. "numbers"
  en: string; // canonical English title (= Sefaria index title)
  he: string; // Hebrew title
  category: SourceCategory;
  addressing: Addressing;
  aliases: string[]; // matched after normalizeKey(); NEVER fuzzy
}

export const KNOWN_WORKS: readonly KnownWork[] = [
  // Torah
  { id: "genesis", en: "Genesis", he: "בראשית", category: "Tanach", addressing: "chapter_verse", aliases: ["bereishit", "bereshit", "breishit", "gen", "בראשית"] },
  { id: "exodus", en: "Exodus", he: "שמות", category: "Tanach", addressing: "chapter_verse", aliases: ["shemot", "shmot", "shemos", "ex", "exod", "שמות"] },
  { id: "leviticus", en: "Leviticus", he: "ויקרא", category: "Tanach", addressing: "chapter_verse", aliases: ["vayikra", "vayyikra", "lev", "ויקרא"] },
  { id: "numbers", en: "Numbers", he: "במדבר", category: "Tanach", addressing: "chapter_verse", aliases: ["bamidbar", "bemidbar", "num", "במדבר"] },
  { id: "deuteronomy", en: "Deuteronomy", he: "דברים", category: "Tanach", addressing: "chapter_verse", aliases: ["devarim", "dvarim", "deut", "deu", "דברים"] },
  // Nach (the ones the app most commonly meets via citations)
  { id: "isaiah", en: "Isaiah", he: "ישעיהו", category: "Tanach", addressing: "chapter_verse", aliases: ["yeshayahu", "yeshaya", "isa", "ישעיהו"] },
  { id: "ezekiel", en: "Ezekiel", he: "יחזקאל", category: "Tanach", addressing: "chapter_verse", aliases: ["yechezkel", "yehezkel", "ezek", "יחזקאל"] },
  { id: "psalms", en: "Psalms", he: "תהלים", category: "Tanach", addressing: "chapter_verse", aliases: ["tehillim", "tehilim", "ps", "psa", "תהלים", "תהילים"] },
  { id: "proverbs", en: "Proverbs", he: "משלי", category: "Tanach", addressing: "chapter_verse", aliases: ["mishlei", "mishle", "prov", "משלי"] },
  { id: "song-of-songs", en: "Song of Songs", he: "שיר השירים", category: "Tanach", addressing: "chapter_verse", aliases: ["shir hashirim", "shir hashshirim", "canticles", "שיר השירים"] },
  { id: "esther", en: "Esther", he: "אסתר", category: "Tanach", addressing: "chapter_verse", aliases: ["megillat esther", "אסתר"] },
  // Talmud Bavli (daf/amud)
  { id: "berakhot", en: "Berakhot", he: "ברכות", category: "Talmud", addressing: "daf_amud", aliases: ["berachot", "brachot", "berachos", "brachos", "ברכות"] },
  { id: "shabbat", en: "Shabbat", he: "שבת", category: "Talmud", addressing: "daf_amud", aliases: ["shabbos", "shabat", "שבת"] },
  { id: "chagigah", en: "Chagigah", he: "חגיגה", category: "Talmud", addressing: "daf_amud", aliases: ["chagiga", "hagigah", "hagiga", "חגיגה"] },
  { id: "megillah", en: "Megillah", he: "מגילה", category: "Talmud", addressing: "daf_amud", aliases: ["megilla", "megila", "מגילה"] },
  { id: "sanhedrin", en: "Sanhedrin", he: "סנהדרין", category: "Talmud", addressing: "daf_amud", aliases: ["סנהדרין"] },
  { id: "menachot", en: "Menachot", he: "מנחות", category: "Talmud", addressing: "daf_amud", aliases: ["menachos", "menahot", "מנחות"] },
  // Mishnah (chapter:mishnah)
  { id: "pirkei-avot", en: "Pirkei Avot", he: "פרקי אבות", category: "Mishnah", addressing: "chapter_mishnah", aliases: ["avot", "avos", "pirkei avos", "ethics of the fathers", "אבות", "פרקי אבות"] },
  // Halacha (siman:seif)
  { id: "mishnah-berurah", en: "Mishnah Berurah", he: "משנה ברורה", category: "Halacha", addressing: "siman_seif", aliases: ["mishna berura", "mishnah berura", "mishna berurah", "משנה ברורה"] },
  { id: "shulchan-aruch-orach-chayim", en: "Shulchan Arukh, Orach Chayim", he: "שולחן ערוך אורח חיים", category: "Halacha", addressing: "siman_seif", aliases: ["shulchan aruch orach chayim", "shulchan aruch oc", "orach chayim", "shulchan arukh orach chayim"] },
];

// Commentators recognized as a "X [on] <book> <ref>" prefix. EXACT matching
// only - Rambam and Ramban are distinct entries and can never be confused
// (commentator-accuracy rule), same for Rashi vs Rashbam.
export interface KnownCommentator {
  id: string;
  en: string; // canonical English name as Sefaria titles it
  he: string;
  aliases: string[];
  worksOn: string[]; // KnownWork ids the composed "<C> on <Book>" title really exists for
}

// `worksOn` is the HONESTY GATE for ref composition: the base-work ids for
// which the composed "<Commentator> on <Book>" title is a REAL Sefaria index
// title. Composition is refused (fail loud) for any pair not listed - we
// never mint a ref for a work that does not exist (e.g. Rambam wrote no
// Torah commentary, and Sefaria titles Alshich/Baal HaTurim/Chizkuni
// differently). An empty list means: the name is recognized (so it can never
// be confused with a similar name) but no composition is allowed yet.
const TORAH = ["genesis", "exodus", "leviticus", "numbers", "deuteronomy"];
const BAVLI = ["berakhot", "shabbat", "chagigah", "megillah", "sanhedrin", "menachot"];

export const KNOWN_COMMENTATORS: readonly KnownCommentator[] = [
  { id: "rashi", en: "Rashi", he: "רש״י", aliases: ["רשי"], worksOn: [...TORAH, ...BAVLI, "isaiah", "ezekiel", "psalms", "proverbs", "song-of-songs", "esther"] },
  { id: "rashbam", en: "Rashbam", he: "רשב״ם", aliases: ["רשבם"], worksOn: [...TORAH] },
  { id: "ramban", en: "Ramban", he: "רמב״ן", aliases: ["nachmanides", "רמבן"], worksOn: [...TORAH] },
  // Rambam (Maimonides) wrote NO Tanach commentary - worksOn stays empty so
  // "Rambam Bereishit 1:1" fails loud instead of minting a nonexistent work.
  // The entry still exists so Rambam can NEVER be mis-matched to Ramban.
  { id: "rambam", en: "Rambam", he: "רמב״ם", aliases: ["maimonides", "רמבם"], worksOn: [] },
  { id: "ibn-ezra", en: "Ibn Ezra", he: "אבן עזרא", aliases: ["אבן עזרא"], worksOn: [...TORAH, "isaiah", "psalms", "esther"] },
  { id: "sforno", en: "Sforno", he: "ספורנו", aliases: ["seforno", "ספורנו"], worksOn: [...TORAH] },
  { id: "or-hachaim", en: "Or HaChaim", he: "אור החיים", aliases: ["ohr hachaim", "or hachayim", "אור החיים"], worksOn: [...TORAH] },
  { id: "kli-yakar", en: "Kli Yakar", he: "כלי יקר", aliases: ["keli yakar", "כלי יקר"], worksOn: [...TORAH] },
  // Sefaria titles Alshich's Torah commentary differently (not "Alshich on
  // <Book>") - recognized, but no composition until the real titles are mapped.
  { id: "alshich", en: "Alshich", he: "אלשיך", aliases: ["alshikh", "אלשיך"], worksOn: [] },
  // Same: Sefaria uses comma-form titles for these two ("Chizkuni, Genesis").
  { id: "baal-haturim", en: "Baal HaTurim", he: "בעל הטורים", aliases: ["baal haturim", "בעל הטורים"], worksOn: [] },
  { id: "chizkuni", en: "Chizkuni", he: "חזקוני", aliases: ["hizkuni", "חזקוני"], worksOn: [] },
  { id: "radak", en: "Radak", he: "רד״ק", aliases: ["רדק"], worksOn: ["genesis", "isaiah", "ezekiel", "psalms"] },
  { id: "ralbag", en: "Ralbag", he: "רלב״ג", aliases: ["gersonides", "רלבג"], worksOn: [] },
  { id: "malbim", en: "Malbim", he: "מלבי״ם", aliases: ["מלבים"], worksOn: ["isaiah"] },
  // NOTE: Targum (Onkelos) is deliberately NOT here - Sefaria titles it
  // "Onkelos Genesis", not "Onkelos on Genesis", so the "<C> on <Book>"
  // synthesis this table feeds would produce a non-Sefaria ref.
  { id: "tosafot", en: "Tosafot", he: "תוספות", aliases: ["tosafos", "tosfot", "תוספות"], worksOn: [...BAVLI] },
];

// ---------------------------------------------------------------------------
// Lookup keys: lowercase, drop periods/quotes/geresh marks, collapse spaces.
// Hebrew input additionally loses nikud/te'amim via stripHebrew (citations.ts).
// ---------------------------------------------------------------------------

// The punctuation class is built from \u-escapes (curly quotes, geresh,
// gershayim) because this repo's formatter mangles those literals (see the
// citations.ts header). Covers: period, ASCII quote/backtick, curly quotes,
// geresh, gershayim, double quote.
const RE_KEY_PUNCT = new RegExp("[.'`\\u2018\\u2019\\u05F3\\u05F4\"]", "g");

function normalizeKey(s: string): string {
  return stripHebrew(s).toLowerCase().replace(RE_KEY_PUNCT, "").replace(/\s+/g, " ").trim();
}

const WORK_BY_KEY = new Map<string, KnownWork>();
for (const w of KNOWN_WORKS) {
  WORK_BY_KEY.set(normalizeKey(w.en), w);
  WORK_BY_KEY.set(normalizeKey(w.he), w);
  for (const a of w.aliases) WORK_BY_KEY.set(normalizeKey(a), w);
}

const COMMENTATOR_BY_KEY = new Map<string, KnownCommentator>();
for (const c of KNOWN_COMMENTATORS) {
  COMMENTATOR_BY_KEY.set(normalizeKey(c.en), c);
  COMMENTATOR_BY_KEY.set(normalizeKey(c.he), c);
  for (const a of c.aliases) COMMENTATOR_BY_KEY.set(normalizeKey(a), c);
}

// Exact-key lookups, exported for the adapter/integrity layers.
export function lookupWork(title: string): KnownWork | null {
  return WORK_BY_KEY.get(normalizeKey(title)) ?? null;
}
export function lookupCommentator(name: string): KnownCommentator | null {
  return COMMENTATOR_BY_KEY.get(normalizeKey(name)) ?? null;
}

// ---------------------------------------------------------------------------
// Hebrew numerals (for generated heRefs): 13 -> yud-gimel with gershayim.
// Uses the standard 15/16 exceptions (tet-vav / tet-zayin, never spelling the
// divine name). The geresh (U+05F3) and gershayim (U+05F4) marks are built
// from \u-escapes - this repo's cp1252 toolchain mangles those literals (see
// the citations.ts header).
// ---------------------------------------------------------------------------

const ONES = ["", "א", "ב", "ג", "ד", "ה", "ו", "ז", "ח", "ט"];
const TENS = ["", "י", "כ", "ל", "מ", "נ", "ס", "ע", "פ", "צ"];
const HUNDREDS = ["", "ק", "ר", "ש", "ת"];
const GERESH = "\u05F3";
const GERSHAYIM = "\u05F4";

export function hebrewNumeral(n: number): string {
  if (!Number.isInteger(n) || n <= 0 || n > 999) return String(n);
  let rest = n;
  let out = "";
  while (rest >= 100) {
    const h = Math.min(4, Math.floor(rest / 100));
    out += HUNDREDS[h];
    rest -= h * 100;
  }
  if (rest === 15) out += "טו"; // tet-vav
  else if (rest === 16) out += "טז"; // tet-zayin
  else {
    out += TENS[Math.floor(rest / 10)];
    out += ONES[rest % 10];
  }
  // punctuate: single letter gets geresh, multi-letter gets gershayim before last
  if (out.length === 1) return out + GERESH;
  return out.slice(0, -1) + GERSHAYIM + out.slice(-1);
}

// Decode a Hebrew numeral token (yud-gimel = 13, with or without gershayim) to
// its number. ACCURACY GUARD: only CANONICAL numeral shapes decode - the token
// must round-trip (hebrewNumeral(value) == token after mark-stripping), so an
// arbitrary Hebrew word or a letters-in-wrong-order run is rejected (null)
// instead of being summed into a fabricated number.
function hebrewNumeralValue(token: string): number | null {
  const v = gematria(token);
  if (v <= 0) return null;
  return stripHebrew(hebrewNumeral(v)) === token ? v : null;
}

// ---------------------------------------------------------------------------
// Section-tail parsing
// ---------------------------------------------------------------------------

interface ParsedTail {
  sections: Array<string | number>;
  toSections?: Array<string | number>;
  isRange: boolean;
}

// Parse the numeric tail of a ref: "13:16", "13:16-18", "13:16-14:2", "12a",
// "12a:5", "13", or Hebrew-numeral forms of the colon shapes (yud-gimel :
// tet-zayin = 13:16). Returns null when the tail is not a recognizable
// address (fail loud upstream).
// hyphen + maqaf + en/em dash, built from \u-escapes (formatter-safe,
// same convention as citations.ts RE_DASH).
const RE_ANY_DASH = new RegExp("[\\u05BE\\u2013\\u2014-]");

function parseTail(tail: string): ParsedTail | null {
  const t = tail.trim();
  if (!t) return null;

  // daf+amud, optionally with sub-segments: "12a", "43b:5"
  const daf = t.match(/^(\d+)([ab])((?::\d+)*)$/);
  if (daf) {
    const sections: Array<string | number> = [parseInt(daf[1], 10), daf[2]];
    if (daf[3]) for (const seg of daf[3].split(":").filter(Boolean)) sections.push(parseInt(seg, 10));
    return { sections, isRange: false };
  }

  // colon-separated numerics with an optional range: "13:16", "13:16-18", "13:16-14:2"
  const range = t.match(/^(\d+(?::\d+)*)-(\d+(?::\d+)*)$/);
  if (range) {
    const from = range[1].split(":").map((x) => parseInt(x, 10));
    let to = range[2].split(":").map((x) => parseInt(x, 10));
    // "13:16-18" means 13:16 through 13:18: inherit the missing leading levels
    if (to.length < from.length) to = [...from.slice(0, from.length - to.length), ...to];
    return { sections: from, toSections: to, isRange: true };
  }

  const plain = t.match(/^\d+(?::\d+)*$/);
  if (plain) return { sections: t.split(":").map((x) => parseInt(x, 10)), isRange: false };

  // Hebrew-numeral colon form (yud-gimel : tet-zayin = 13:16). Guards against
  // fabrication: dashes (Hebrew ranges are NOT supported - stripHebrew turns
  // the dash into a space, which would merge both ends into one token and sum
  // their gematria into a different verse), and any token that is not a
  // canonical numeral shape (round-trip check in hebrewNumeralValue).
  if (RE_ANY_DASH.test(t)) return null;
  const hebParts = stripHebrew(t).split(":").map((p) => p.trim());
  if (hebParts.length >= 1 && hebParts.every((p) => p.length > 0 && !/\s/.test(p))) {
    const nums = hebParts.map(hebrewNumeralValue);
    if (nums.every((n): n is number => n !== null)) {
      return { sections: nums, isRange: false };
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Address construction per the work's addressing system
// ---------------------------------------------------------------------------

function buildAddress(work: KnownWork, tail: ParsedTail): PassageAddress {
  const s = tail.sections;
  switch (work.addressing) {
    case "chapter_verse": {
      const chapter = typeof s[0] === "number" ? s[0] : 0;
      const verse = typeof s[1] === "number" ? s[1] : undefined;
      if (s.length > 2) return { system: "paragraph", sectionPath: [...s] };
      return { system: "chapter_verse", chapter, verse };
    }
    case "daf_amud": {
      const daf = typeof s[0] === "number" ? s[0] : 0;
      const amud = s[1] === "b" ? "b" : "a";
      return { system: "daf_amud", daf, amud };
    }
    case "siman_seif":
      return { system: "siman_seif", siman: s[0], seif: s[1] };
    case "chapter_mishnah": {
      const chapter = typeof s[0] === "number" ? s[0] : 0;
      const mishnah = typeof s[1] === "number" ? s[1] : 0;
      return { system: "chapter_mishnah", chapter, mishnah };
    }
  }
}

function refTypeFor(work: KnownWork, tail: ParsedTail): Passage["refType"] {
  if (tail.isRange) return "range";
  switch (work.addressing) {
    case "chapter_verse":
      if (tail.sections.length === 1) return "chapter";
      if (tail.sections.length === 2) return "verse";
      return "paragraph";
    case "daf_amud":
      return "daf";
    case "siman_seif":
      return tail.sections.length >= 2 ? "seif" : "siman";
    case "chapter_mishnah":
      return "mishnah";
  }
}

// Render sections back to the canonical English tail ("13:16", "12a", "13:16-18").
function renderTail(work: KnownWork, tail: ParsedTail): string {
  if (work.addressing === "daf_amud") {
    const [daf, amud, ...rest] = tail.sections;
    return `${daf}${amud}${rest.length ? ":" + rest.join(":") : ""}`;
  }
  const head = tail.sections.join(":");
  if (!tail.isRange || !tail.toSections) return head;
  // shorten the range tail where leading levels repeat: 13:16-13:18 -> 13:16-18
  const from = tail.sections;
  const to = tail.toSections;
  let i = 0;
  while (i < to.length - 1 && i < from.length && from[i] === to[i]) i++;
  return `${head}-${to.slice(i).join(":")}`;
}

// Hebrew tail for chapter_verse works: "13:16" -> "yud-gimel:tet-zayin".
function renderHeTail(tail: ParsedTail): string | null {
  if (tail.isRange) return null; // keep heRef honest: ranges not rendered yet
  if (!tail.sections.every((x) => typeof x === "number")) return null;
  return (tail.sections as number[]).map(hebrewNumeral).join(":");
}

// ---------------------------------------------------------------------------
// The normalizer
// ---------------------------------------------------------------------------

// Try to split "<commentator> [on] <rest>" using the exact-match commentator
// table. Longest-prefix match over 1..4 leading words.
function splitCommentator(input: string): { commentator: KnownCommentator; rest: string } | null {
  const words = input.trim().split(/\s+/);
  for (let n = Math.min(4, words.length - 1); n >= 1; n--) {
    const head = words.slice(0, n).join(" ");
    const c = COMMENTATOR_BY_KEY.get(normalizeKey(head));
    if (c) {
      let rest = words.slice(n).join(" ");
      rest = rest.replace(/^(on|al|על)\s+/i, ""); // optional "on" / "al"
      return { commentator: c, rest };
    }
  }
  return null;
}

// Try to split "<book title> <tail>" using the exact-match works table.
// Greedy: longest leading word-run that matches a known work.
function splitWork(input: string): { work: KnownWork; tail: string } | null {
  const words = input.trim().split(/\s+/);
  for (let n = Math.min(6, words.length - 1); n >= 1; n--) {
    const head = words.slice(0, n).join(" ");
    const w = WORK_BY_KEY.get(normalizeKey(head));
    if (w) return { work: w, tail: words.slice(n).join(" ") };
  }
  return null;
}

const fail = (input: string, error: string): NormalizedRefResult => ({ input, valid: false, error });

// Normalize a free-form ref string to its canonical form. Deterministic table
// + parser; unknown works/commentators or unparseable tails FAIL with an
// explicit error - never a silent guess.
export function normalizeRef(input: string): NormalizedRefResult {
  const raw = input?.trim?.() ?? "";
  if (!raw) return fail(input ?? "", "Empty reference");

  const warnings: string[] = [];

  const split = splitCommentator(raw);
  const bookPart = split ? split.rest : raw;
  const workSplit = splitWork(bookPart);
  if (!workSplit) {
    return fail(raw, "Could not normalize reference: unknown work/book");
  }
  const { work, tail: tailStr } = workSplit;
  const tail = parseTail(tailStr);
  if (!tail) {
    return fail(raw, `Could not parse the location "${tailStr}" for ${work.en}`);
  }

  // Talmud refs MUST carry an amud - "Chagigah 13" or "Chagigah 13:5" would
  // otherwise render a fabricated daf. Fail loud, never guess an amud.
  if (work.addressing === "daf_amud" && tail.sections[1] !== "a" && tail.sections[1] !== "b") {
    return fail(raw, `Talmud refs need an amud, e.g. "${work.en} ${tail.sections[0]}a"`);
  }

  const enTail = renderTail(work, tail);
  const heTail = renderHeTail(tail);

  let canonicalRef: string;
  let heRef: string | undefined;
  let workId: string;
  let category: SourceCategory;

  if (split) {
    // Commentary ref: "<Commentator> on <Book> <tail>" - composed ONLY when
    // the pair is in the commentator's worksOn allowlist, i.e. the composed
    // title is a real Sefaria index. We never mint a ref for a work that
    // does not exist (Rambam wrote no Torah commentary; Alshich is titled
    // differently on Sefaria).
    const c = split.commentator;
    if (!c.worksOn.includes(work.id)) {
      return fail(raw, `No known work "${c.en} on ${work.en}" - not composing an unverified ref`);
    }
    canonicalRef = `${c.en} on ${work.en} ${enTail}`;
    heRef = heTail ? `${c.he} על ${work.he} ${heTail}` : undefined;
    workId = `${c.id}-on-${work.id}`;
    category = "Commentary";
  } else {
    canonicalRef = `${work.en} ${enTail}`;
    heRef = heTail ? `${work.he} ${heTail}` : undefined;
    workId = work.id;
    category = work.category;
  }

  if (tail.isRange) warnings.push("Range refs are normalized without a Hebrew ref");

  const normalizedRef: NormalizedRef = {
    originalInput: raw,
    canonicalRef,
    heRef,
    workId,
    sections: tail.sections,
    toSections: tail.toSections,
    isRange: tail.isRange,
    normalizedBy: "manual_map",
    confidence: "verified",
    warnings: warnings.length ? warnings : undefined,
  };

  return {
    input: raw,
    valid: true,
    canonicalRef,
    heRef,
    workId,
    category,
    normalizedRef,
    warnings: warnings.length ? warnings : undefined,
  };
}

// ---------------------------------------------------------------------------
// Canonical-ref structural parse (no alias table): used by the adapter to
// derive work-title / sections / address from refs Sefaria ALREADY returned in
// canonical form ("Rashi on Numbers 13:16", "Menachot 43b:5"). Returns null
// when the ref has no parseable numeric tail (caller decides how to degrade).
// ---------------------------------------------------------------------------

export interface ParsedCanonicalRef {
  workTitle: string; // "Rashi on Numbers" / "Menachot"
  baseWorkTitle: string | null; // "Numbers" for "Rashi on Numbers"; null when not "X on Y"
  sections: Array<string | number>;
  toSections?: Array<string | number>;
  isRange: boolean;
  refType: Passage["refType"];
  address: PassageAddress;
}

export function parseCanonicalRef(ref: string): ParsedCanonicalRef | null {
  const m = ref.trim().match(/^(.*?)\s+(\d+[ab]?(?::\d+)*(?:-\d+(?::\d+)*)?)$/);
  if (!m) return null;
  const workTitle = m[1].trim();
  const tail = parseTail(m[2]);
  if (!tail || !workTitle) return null;

  const onSplit = workTitle.split(/ on /i);
  const baseWorkTitle = onSplit.length > 1 ? onSplit[onSplit.length - 1].trim() : null;

  // Addressing: known-work lookup wins; else infer daf_amud from the tail shape.
  const known = lookupWork(workTitle) ?? (baseWorkTitle ? lookupWork(baseWorkTitle) : null);
  const isDaf = typeof tail.sections[1] === "string";
  // A known daf-addressed work with an amud-less tail is unparseable - guessing
  // an amud would fabricate a daf ("Chagigah 13" is not an address). Fail loud.
  if (known?.addressing === "daf_amud" && !isDaf) return null;
  const addressing: Addressing = known?.addressing ?? (isDaf ? "daf_amud" : "chapter_verse");
  const pseudo: KnownWork = known ?? {
    id: slugifyTitle(workTitle),
    en: workTitle,
    he: "",
    category: "Other",
    addressing,
    aliases: [],
  };
  return {
    workTitle,
    baseWorkTitle,
    sections: tail.sections,
    toSections: tail.toSections,
    isRange: tail.isRange,
    refType: refTypeFor(pseudo, tail),
    address: buildAddress(pseudo, tail),
  };
}

// Stable, inspectable id from a work title: "Rashi on Numbers" -> "rashi-on-numbers".
export function slugifyTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9א-ת]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Does the structural base of an "X on Y"-shaped ref match `currentRef`?
// Tolerates segment-depth differences ("Numbers 13" vs "Numbers 13:16") but never
// crosses works. The ONE home for the structural-commentary proof rule, shared by the
// source-graph adapter (which asserts it) and its validation gate (which checks it) so
// the two can never drift. Exported for tests.
export function structuralBaseMatches(itemRef: string, currentRef: string): boolean {
  if (!/ on /i.test(itemRef)) return false;
  const base = itemRef.split(/ on /i).pop()?.trim() ?? "";
  if (!base) return false;
  return base === currentRef || base.startsWith(`${currentRef}:`) || currentRef.startsWith(`${base}:`);
}
